import { useMemo } from "react";

import useAudio from "@/utils/hooks/useAudio";
import Valkyrie, {
  viArrowDownToLine,
  viArrowRotateRight,
  viPause,
  viPlay,
  viVolume0,
  viVolume1,
  viVolume2,
  viVolume3,
  viVolumeSlash,
} from "@sippy-platform/valkyrie";

import { Slider } from "@base-ui/react";
import { Button, IconButton } from "@mui/material";
import { cn } from "cn";

import { type Transcript } from "./transcriptParser";

export function TranscriptPlayer({ src = "", transcript }: { src?: string; transcript?: Transcript }) {
  const {
    playable,
    changeVolume,
    duration,
    isMuted,
    playPause,
    progress,
    secondsToTime,
    seek,
    seekFinish,
    status,
    toggleMute,
    toggleVolume,
    volume,
  } = useAudio(src);

  const { timeline, timelineDuration } = useMemo(() => {
    const sortedSteps = [...(transcript?.steps || [])].sort((a, b) => a.timecode - b.timecode);
    const lastStepTimecode = sortedSteps[sortedSteps.length - 1]?.timecode || 0;
    const calculatedDuration = Math.max(duration, lastStepTimecode + 1, 1);
    const nextTimeline: {
      caller: { start: number; end: number; speakerId: number }[];
      callee: { start: number; end: number; speakerId: number }[];
    } = {
      caller: [],
      callee: [],
    };

    for (let index = 0; index < sortedSteps.length; index += 1) {
      const step = sortedSteps[index];
      const nextStepTimecode = sortedSteps[index + 1]?.timecode ?? calculatedDuration;
      const start = Math.max(0, Math.min(step.timecode, calculatedDuration));
      const end = Math.max(start, Math.min(nextStepTimecode, calculatedDuration));

      if (end > start) {
        nextTimeline[step.track].push({ start, end, speakerId: step.speakerId });
      }
    }

    return {
      timeline: nextTimeline,
      timelineDuration: calculatedDuration,
    };
  }, [duration, transcript?.steps]);

  return (
    <div className="@container/player flex flex-col gap-1.5 rounded-lg border border-zinc-950/10 p-2 dark:border-white/5">
      <div className="flex items-center justify-between gap-1">
        <div className="flex items-center gap-1">
          <IconButton onClick={playPause} disabled={!playable} size="small" color="inherit">
            <Valkyrie icon={status === "stopped" ? viArrowRotateRight : status === "paused" ? viPlay : viPause} />
          </IconButton>

          <IconButton
            onClick={toggleVolume}
            size="small"
            color="inherit"
            className="inline-flex @lg/player:hidden!"
            disabled={!playable}
          >
            <Valkyrie
              icon={
                isMuted || volume === 0
                  ? viVolumeSlash
                  : volume > 75
                    ? viVolume3
                    : volume > 50
                      ? viVolume2
                      : volume > 25
                        ? viVolume1
                        : viVolume0
              }
            />
          </IconButton>

          <div className="hidden w-24 grid-cols-[min-content_auto] items-center gap-2 @lg/player:inline-grid">
            <IconButton onClick={toggleMute} size="small" color="inherit" disabled={!playable}>
              <Valkyrie
                icon={
                  isMuted || volume === 0
                    ? viVolumeSlash
                    : volume > 75
                      ? viVolume3
                      : volume > 50
                        ? viVolume2
                        : volume > 25
                          ? viVolume1
                          : viVolume0
                }
              />
            </IconButton>

            <Slider.Root
              value={isMuted ? 0 : volume}
              onValueChange={changeVolume}
              min={0}
              max={100}
              thumbAlignment="edge"
            >
              <Slider.Control className="flex touch-none items-center select-none">
                <Slider.Track className="h-2 w-full rounded-full bg-zinc-200 select-none dark:bg-zinc-700">
                  <Slider.Indicator className="rounded-s-full bg-[#0068d8] select-none" />
                  <Slider.Thumb
                    aria-label="Volume"
                    className="h-4 w-2 rounded-full border-2 border-white bg-[#0068d8] select-none has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-zinc-950 dark:border-[#1f1f24] dark:has-focus-visible:outline-white"
                  />
                </Slider.Track>
              </Slider.Control>
            </Slider.Root>
          </div>
        </div>

        <p className="text-sm text-nowrap tabular-nums">
          {secondsToTime(progress)}
          <span className="dark:text-zinc-400"> / {secondsToTime(duration)}</span>
        </p>

        <Button
          href={src}
          size="small"
          color="inherit"
          disabled={!src}
          download
          startIcon={<Valkyrie icon={viArrowDownToLine} />}
        >
          Download
        </Button>
      </div>

      <div className="flex flex-col gap-1">
        <Slider.Root
          value={progress}
          onValueChange={seek}
          onValueCommitted={seekFinish}
          min={0}
          max={duration}
          disabled={!playable}
          thumbAlignment="edge"
        >
          <Slider.Control className="z-10 mb-1 flex touch-none items-center select-none">
            <Slider.Track className="h-2 w-full overflow-visible rounded-full bg-zinc-200 select-none dark:bg-zinc-700">
              <Slider.Indicator className="rounded-s-full bg-[#0068d8] select-none" />
              <Slider.Thumb
                aria-label="Track progress"
                className={cn(
                  "z-10 h-4 w-2 rounded-full border-2 border-white bg-[#0068d8] select-none has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-zinc-950 dark:border-[#1f1f24] dark:has-focus-visible:outline-white",
                  transcript &&
                    !transcript.failed &&
                    "after:pointer-events-none after:absolute after:top-full after:left-1/2 after:z-10 after:h-6 after:w-1.5 after:-translate-x-1/2 after:rounded-full after:border-2 after:border-white after:bg-[#0068d8] dark:after:border-[#1f1f24]",
                )}
              />
            </Slider.Track>
          </Slider.Control>
        </Slider.Root>

        {transcript && !transcript.failed && (
          <>
            <Slider.Root
              value={progress}
              onValueChange={seek}
              onValueCommitted={seekFinish}
              min={0}
              max={duration}
              disabled={!playable}
              thumbAlignment="edge"
            >
              <Slider.Control className="flex touch-none items-center select-none">
                <Slider.Track className="h-1 w-full rounded-full bg-zinc-200 select-none dark:bg-zinc-700">
                  {timeline.callee.map((segment, index) => {
                    const participantColorIndex = ((segment.speakerId - 1) % 10) + 1;

                    return (
                      <div
                        key={`callee-${segment.start}-${segment.end}-${index}`}
                        className={cn("absolute inset-y-0 rounded-full", {
                          "bg-lime-600 dark:bg-lime-400": participantColorIndex === 1,
                          "bg-sky-600 dark:bg-sky-400": participantColorIndex === 2,
                          "bg-amber-600 dark:bg-amber-400": participantColorIndex === 3,
                          "bg-fuchsia-600 dark:bg-fuchsia-400": participantColorIndex === 4,
                          "bg-red-600 dark:bg-red-400": participantColorIndex === 5,
                          "bg-violet-600 dark:bg-violet-400": participantColorIndex === 6,
                          "bg-green-600 dark:bg-green-400": participantColorIndex === 7,
                          "bg-yellow-600 dark:bg-yellow-400": participantColorIndex === 8,
                          "bg-blue-600 dark:bg-blue-400": participantColorIndex === 9,
                          "bg-pink-600 dark:bg-pink-400": participantColorIndex === 10,
                        })}
                        style={{
                          left: `${(segment.start / timelineDuration) * 100}%`,
                          width: `${Math.max(((segment.end - segment.start) / timelineDuration) * 100, 0.4)}%`,
                        }}
                      />
                    );
                  })}
                </Slider.Track>
              </Slider.Control>
            </Slider.Root>

            <Slider.Root
              value={progress}
              onValueChange={seek}
              onValueCommitted={seekFinish}
              min={0}
              max={duration}
              disabled={!playable}
              thumbAlignment="edge"
            >
              <Slider.Control className="flex touch-none items-center select-none">
                <Slider.Track className="h-1 w-full rounded-full bg-zinc-200 select-none dark:bg-zinc-700">
                  {timeline.caller.map((segment, index) => {
                    const participantColorIndex = ((segment.speakerId - 1) % 10) + 1;

                    return (
                      <div
                        key={`caller-${segment.start}-${segment.end}-${index}`}
                        className={cn("absolute inset-y-0 rounded-full", {
                          "bg-lime-600 dark:bg-lime-400": participantColorIndex === 1,
                          "bg-sky-600 dark:bg-sky-400": participantColorIndex === 2,
                          "bg-amber-600 dark:bg-amber-400": participantColorIndex === 3,
                          "bg-fuchsia-600 dark:bg-fuchsia-400": participantColorIndex === 4,
                          "bg-red-600 dark:bg-red-400": participantColorIndex === 5,
                          "bg-violet-600 dark:bg-violet-400": participantColorIndex === 6,
                          "bg-green-600 dark:bg-green-400": participantColorIndex === 7,
                          "bg-yellow-600 dark:bg-yellow-400": participantColorIndex === 8,
                          "bg-blue-600 dark:bg-blue-400": participantColorIndex === 9,
                          "bg-pink-600 dark:bg-pink-400": participantColorIndex === 10,
                        })}
                        style={{
                          left: `${(segment.start / timelineDuration) * 100}%`,
                          width: `${Math.max(((segment.end - segment.start) / timelineDuration) * 100, 0.4)}%`,
                        }}
                      />
                    );
                  })}
                </Slider.Track>
              </Slider.Control>
            </Slider.Root>
          </>
        )}
      </div>
    </div>
  );
}
